import {
  renderCachedUserAvatarUrl,
  revokeCachedUserAvatarObjectUrl,
  UserAvatarCacheProvider,
  type UserAvatarCacheService,
} from '@blocksuite/affine-shared/services';
import { SignalWatcher, WithDisposable } from '@blocksuite/global/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css } from '@emotion/css';
import { html, type TemplateResult } from 'lit';
import { state } from 'lit/decorators.js';

import {
  dataViewI18nT,
  getDataViewRuntimeLocale,
} from '../../../../core/i18n.js';
import type { GanttSingleView } from '../../gantt-view-manager.js';
import {
  readCellText,
  resolveBarChip,
  type ResolvedBarAssignee,
} from '../cell-display.js';

/**
 * Floating card that appears after a short hover delay on a bar and
 * shows the full row information — title, assignee, start, end,
 * status, progress — regardless of which chips the user chose to
 * show on the bar itself. Always a superset of the bar's chip row so
 * truncated values stay one-pointer-rest away.
 *
 * Mounted once under `document.body` by the bar-renderer (avoids
 * `position: fixed` being distorted by Edgeless's CSS transform on
 * the scene) and driven imperatively via `show` / `hide` — no signal
 * wiring is needed because the tooltip lives on the transient hover
 * path, not the render path.
 */
export class GanttBarHoverTooltip extends SignalWatcher(
  WithDisposable(ShadowlessElement)
) {
  @state()
  accessor visible = false;

  @state()
  accessor clientX = 0;

  @state()
  accessor clientY = 0;

  @state()
  accessor rows: ReadonlyArray<TooltipRow> = [];

  private avatarCache: UserAvatarCacheService | null = null;

  /**
   * Fill the tooltip from a row's cells and schedule its display.
   * The bar renderer owns debouncing (hover delay) and the hide
   * side — this method is side-effect-free beyond the state writes.
   */
  show(opts: {
    clientX: number;
    clientY: number;
    view: GanttSingleView;
    rowId: string;
  }): void {
    this.clientX = opts.clientX;
    this.clientY = opts.clientY;
    this.avatarCache = opts.view.serviceGet(UserAvatarCacheProvider) ?? null;
    this.rows = buildRows(opts.view, opts.rowId);
    this.visible = true;
  }

  hide(): void {
    this.visible = false;
  }

  override render(): TemplateResult {
    if (!this.visible || this.rows.length === 0) return html``;
    // Render once at a placeholder position, then flip-place after
    // layout so we never show a flash in the wrong quadrant. Final
    // clamping to the viewport is handled in `updated` below.
    return html`
      <div
        class=${tooltipCardStyle}
        style="left:${this.clientX}px; top:${this.clientY}px;"
      >
        ${this.rows.map(
          r => html`
            <div class=${rowStyle}>
              <div class=${labelStyle}>${r.label}</div>
              <div class=${valueStyle}>${renderValue(r, this.avatarCache)}</div>
            </div>
          `
        )}
      </div>
    `;
  }

  protected override updated(): void {
    if (!this.visible) return;
    const card = this.querySelector<HTMLDivElement>(`.${tooltipCardStyle}`);
    if (!card) return;
    // Clamp to viewport: flip to the left/above the cursor when the
    // card would overflow the right/bottom edge.
    const rect = card.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pad = 8;
    const preferRight = this.clientX + pad + rect.width <= vw - pad;
    const preferBelow = this.clientY + pad + rect.height <= vh - pad;
    const left = preferRight
      ? this.clientX + pad
      : Math.max(pad, this.clientX - pad - rect.width);
    const top = preferBelow
      ? this.clientY + pad
      : Math.max(pad, this.clientY - pad - rect.height);
    card.style.left = `${left}px`;
    card.style.top = `${top}px`;
  }
}

type TooltipRow =
  | { kind: 'text'; label: string; value: string }
  | { kind: 'members'; label: string; members: ResolvedBarAssignee[] };

function renderTooltipMemberAvatar(
  member: ResolvedBarAssignee,
  avatarCache: UserAvatarCacheService | null
) {
  if (!member.avatarUrl) {
    return html`<span class=${avatarInitialsStyle}>${member.initials}</span>`;
  }

  return renderCachedUserAvatarUrl({
    avatarCache,
    avatarUrl: member.avatarUrl,
    userId: member.id,
    render: (url, cached) =>
      html`<img
        class=${avatarImgStyle}
        src=${url}
        alt=""
        @load=${() => revokeCachedUserAvatarObjectUrl(url, cached)}
        @error=${() => revokeCachedUserAvatarObjectUrl(url, cached)}
      />`,
  });
}

/**
 * Resolve the six gantt roles to tooltip rows. Skips empty values so
 * the card stays compact — an unassigned bar doesn't need a blank
 * "Assignee: —" row. Assignee is resolved through the same user
 * service pipeline as the bar chips (`resolveBarChip`) so the tooltip
 * shows a real name + avatar instead of the raw member id the
 * property's `stringValue$` returns.
 */
function buildRows(view: GanttSingleView, rowId: string): Array<TooltipRow> {
  const out: Array<TooltipRow> = [];
  const titleCol = view.mainProperties$.value.titleColumn;
  if (titleCol) {
    const t =
      readCellText(view, rowId, titleCol).trim() ||
      dataViewI18nT('gantt.bar.untitled');
    out.push({
      kind: 'text',
      label: dataViewI18nT('gantt.bar.title'),
      value: t,
    });
  }

  // Assignee — resolve member ids via the same pipeline the bar chips
  // use. `resolveBarChip` with 'assignee' + 'both' returns the full
  // members array (id → name + avatar url + initials). When the
  // service hasn't resolved yet the fallback is the raw id, matching
  // the cell behaviour; the tooltip still reads as an attempt at a
  // name rather than a blank row.
  const chip = resolveBarChip(view, rowId, 'assignee', 'both');
  if (chip && chip.members && chip.members.length > 0) {
    out.push({
      kind: 'members',
      label: dataViewI18nT('gantt.bar.assignee'),
      members: chip.members,
    });
  }

  const startCol = view.startDateCol$.value;
  if (startCol) {
    const raw = view.cellGetOrCreate(rowId, startCol).jsonValue$.value;
    const d = toDate(raw);
    if (d) {
      out.push({
        kind: 'text',
        label: dataViewI18nT('gantt.bar.start'),
        value: formatTooltipDate(d),
      });
    }
  }

  const endCol = view.endDateCol$.value;
  if (endCol) {
    const raw = view.cellGetOrCreate(rowId, endCol).jsonValue$.value;
    const d = toDate(raw);
    if (d) {
      out.push({
        kind: 'text',
        label: dataViewI18nT('gantt.bar.end'),
        value: formatTooltipDate(d),
      });
    }
  }

  const statusCol = view.statusCol$.value;
  if (statusCol) {
    const v = readCellText(view, rowId, statusCol).trim();
    if (v) {
      out.push({
        kind: 'text',
        label: dataViewI18nT('gantt.bar.status'),
        value: v,
      });
    }
  }

  const progressCol = view.progressCol$.value;
  if (progressCol) {
    const v = readCellText(view, rowId, progressCol).trim();
    if (v) {
      const n = Number(v);
      const pretty =
        Number.isFinite(n) && !v.includes('%')
          ? `${Math.round(n * (n <= 1 ? 100 : 1))}%`
          : v;
      out.push({
        kind: 'text',
        label: dataViewI18nT('gantt.bar.progress'),
        value: pretty,
      });
    }
  }

  return out;
}

function formatTooltipDate(date: Date): string {
  return new Intl.DateTimeFormat(getDataViewRuntimeLocale(), {
    dateStyle: 'medium',
  }).format(date);
}

function renderValue(
  row: TooltipRow,
  avatarCache: UserAvatarCacheService | null
): TemplateResult {
  if (row.kind === 'text') return html`${row.value}`;
  return html`
    <div class=${membersStyle}>
      ${row.members.map(
        m => html`
          <span class=${memberChipStyle}>
            ${renderTooltipMemberAvatar(m, avatarCache)}
            <span class=${memberNameStyle}>${m.name}</span>
          </span>
        `
      )}
    </div>
  `;
}

function toDate(raw: unknown): Date | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) return new Date(raw);
  if (typeof raw === 'string' && raw) {
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-gantt-bar-hover-tooltip': GanttBarHoverTooltip;
  }
}

// --- Styles -------------------------------------------------------------

const tooltipCardStyle = css({
  position: 'fixed',
  zIndex: 1001,
  minWidth: '220px',
  maxWidth: '320px',
  padding: '10px 12px',
  background: 'var(--streamlain-background-primary-color)',
  border: '1px solid var(--streamlain-border-color)',
  borderRadius: '8px',
  boxShadow: 'var(--streamlain-shadow-2)',
  color: 'var(--streamlain-text-primary-color)',
  fontSize: '12px',
  lineHeight: 1.35,
  pointerEvents: 'none',
  display: 'grid',
  gap: '6px',
});

const rowStyle = css({
  display: 'grid',
  gridTemplateColumns: '70px 1fr',
  gap: '8px',
  alignItems: 'baseline',
});

const labelStyle = css({
  fontSize: '10px',
  fontWeight: 600,
  color: 'var(--streamlain-text-secondary-color)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
});

const valueStyle = css({
  fontWeight: 500,
  overflowWrap: 'anywhere',
});

// Assignee list — wraps on long member lists and keeps each chip
// (avatar + name) as an inline-flex atom so nothing breaks between
// the avatar and its name.
const membersStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px 10px',
  alignItems: 'center',
});

const memberChipStyle = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
});

const avatarImgStyle = css({
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  objectFit: 'cover',
  flexShrink: 0,
});

const avatarInitialsStyle = css({
  width: '18px',
  height: '18px',
  borderRadius: '50%',
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--streamlain-background-tertiary-color)',
  color: 'var(--streamlain-text-secondary-color)',
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.02em',
});

const memberNameStyle = css({
  fontWeight: 500,
});
